import { Box, CircularProgress, Tooltip } from '@mui/material';
import { AlertTriangle } from 'lucide-react';
import type { DatasetEnvironmentRegistry } from '~/engine/scene/DatasetEnvironmentRegistry';
import {
    ImageStreamStatus,
    useImageStreamStatuses,
} from '~/hooks/image/useImageStreamProgress';
import { READOUT_FONT } from '~/viewportGrid/chrome';
import { DrawnImage } from '~/viewportGrid/adapters/types';

/** Icon edge in the title bar, in CSS pixels. */
const ICON_SIZE_PX = 11;

/** Parts the counts of the images in one state. */
const COUNT_SEPARATOR = ' / ';

/** One incomplete image of a tile, with the pair label that names it. */
interface NamedStatus {
    readonly status: ImageStreamStatus;
    readonly pairLabel: string | undefined;
}

interface StreamStateGroupProps {
    /** The tile's images in one state, all loading or all ended short. */
    readonly members: ReadonlyArray<NamedStatus>;
    /** Whether the counts are announced to assistive technology as they change. */
    readonly announces: boolean;
}

/**
 * The tile's images that share one retrieval state, under one badge: while
 * slices arrive, a ring that fills with the share of their slices that has
 * arrived; once retrieval ended short, a warning, for as long as the image
 * is open. Each image adds its count out of its total, named by its pair
 * label when the tile draws two. The badge word keeps the counts from being
 * read as the slice readout beside them. The colours are the theme's
 * primary and error, never the amber of the interpolated-slices badge, so
 * neither state reads as that one.
 */
function StreamStateGroup({ members, announces }: StreamStateGroupProps) {
    const [{ status: first }] = members;
    const isFinal = first.kind === 'incomplete';
    const loaded = members.reduce(
        (sum, { status }) => sum + status.progress.loadedSliceCount,
        0
    );
    const expected = members.reduce(
        (sum, { status }) => sum + status.progress.expectedSliceCount,
        0
    );
    const percent = expected > 0 ? (loaded / expected) * 100 : 0;
    const counts = members
        .map(({ status, pairLabel }) =>
            pairLabel === undefined
                ? status.counts
                : `${pairLabel} ${status.counts}`
        )
        .join(COUNT_SEPARATOR);
    const description = members
        .map(({ status, pairLabel }) =>
            pairLabel === undefined
                ? status.description
                : `${pairLabel}: ${status.description}`
        )
        .join(' ');
    return (
        <Tooltip title={description} placement="bottom-start">
            <Box
                component="span"
                data-testid={
                    isFinal ? 'tile-image-incomplete' : 'tile-image-loading'
                }
                aria-label={description}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    flexShrink: 0,
                    color: isFinal ? 'error.main' : 'primary.main',
                    fontFamily: READOUT_FONT,
                    fontSize: 11,
                    whiteSpace: 'nowrap',
                }}
            >
                {isFinal ? (
                    <AlertTriangle size={ICON_SIZE_PX} aria-hidden />
                ) : (
                    <CircularProgress
                        variant="determinate"
                        value={percent}
                        size={ICON_SIZE_PX}
                        thickness={6}
                        color="primary"
                        aria-hidden
                    />
                )}
                <Box component="span" role={announces ? 'status' : undefined}>
                    {`${first.badge} ${counts}`}
                </Box>
            </Box>
        </Tooltip>
    );
}

interface TileStreamStatusProps {
    /** The images the tile draws, the row's own first. */
    readonly images: ReadonlyArray<DrawnImage>;
    /**
     * Whether this tile announces the counts. One tile per row does, so a
     * row of three planes does not read every change out three times.
     */
    readonly leadsRow: boolean;
    readonly registry: DatasetEnvironmentRegistry;
}

/**
 * The retrieval state of the images a tile draws, for its title bar: the
 * images that ended short under one warning, then the images still loading
 * under one ring, and nothing once every image is complete. The counts are
 * in acquired images, not in the tile's own slices, so they stay apart from
 * the slice readout. Each image comes from the registry entry that also
 * supplies the tile's volume and fused secondary, so a tile never draws an
 * image this cannot find.
 */
export default function TileStreamStatus({
    images,
    leadsRow,
    registry,
}: TileStreamStatusProps) {
    const statuses = useImageStreamStatuses(
        images.map(
            ({ seriesInstanceUid }) =>
                registry.entryFor(seriesInstanceUid)?.image
        )
    );
    const named = images.flatMap(({ pairLabel }, index): NamedStatus[] => {
        const status = statuses[index];
        return status ? [{ status, pairLabel }] : [];
    });
    if (named.length === 0) {
        return null;
    }
    const endedShort = named.filter(
        ({ status }) => status.kind === 'incomplete'
    );
    const loading = named.filter(({ status }) => status.kind === 'loading');
    return (
        <Box
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
        >
            {endedShort.length > 0 ? (
                <StreamStateGroup members={endedShort} announces={leadsRow} />
            ) : null}
            {loading.length > 0 ? (
                <StreamStateGroup members={loading} announces={leadsRow} />
            ) : null}
        </Box>
    );
}
